import type { UserInfo } from '@d/auth/helper/api'
import type { RecordStringStorageNodeOrErrorValue } from '@d/sdk'
import type { RecordStringStorageNodeArrayOrErrorValue, ReferencesOutput } from '@d/sdk/autogenerated'
import type { MimeCategory } from '@d/util'
import type { CacheEntry } from './cache-handler'
import { getUserInfo } from '@d/auth/helper/api'
import { storageApi, StorageNodeTypeEnum } from '@d/sdk'
import { ResponseError } from '@d/sdk/autogenerated'
import { isCategory } from '@d/util'
import { PathGuide } from '@d/util/pathGuide'
import equal from 'fast-deep-equal'
import { CacheGroup, CacheHandler } from './cache-handler'
import { RetrieveCollector } from './retrieveCollector'

function cdUp(path: string) {
  return path.split('/').slice(0, -1).join('/')
}
const rcGet = new RetrieveCollector<RecordStringStorageNodeOrErrorValue>(
  async paths => await storageApi.gets({ paths }),
)

const rcList = new RetrieveCollector<
  RecordStringStorageNodeArrayOrErrorValue
>(async paths => await storageApi.lists({ paths }))

export type StorageNode = Awaited<ReturnType<typeof storageApi.get>>

export function pathKeyProcessor(key: string): string {
  return PathGuide.from(decodeURIComponent(key)).toString()
}
// #region StorageDownloadCache
interface _StorageDownloadCacheType { url: string, blob: Blob }

export const StorageDownloadCache = new CacheHandler<_StorageDownloadCacheType>(
  'StorageDownload',
  async (key) => {
    try {
      const blob = await storageApi.download(key)
      if (blob) {
        return { url: window.URL.createObjectURL(blob), blob }
      }
      else {
        return null
      }
    }
    catch {
      return null
    }
  },
  {
    beforeInvalidate(key, value) {
      if (value && !('failed' in value)) {
        window.URL.revokeObjectURL(value.url)
      }
    },
    keyProcessor: pathKeyProcessor,
  },
)
// #endregion

// #region StorageListCache
export const StorageListCache = new CacheHandler<StorageNode[]>(
  'StorageList',
  async (key: string) => {
    try {
      const res = await rcList.get(key)
      if (!res) {
        console.error(`rcList.get("${key}") returned undefined/null result`)
        return []
      }

      // Type checking and error handling
      if (!Array.isArray(res) && typeof res === 'object' && 'error' in res) {
        console.error(`Error from rcList.get("${key}"):`, res.error)
        return []
      }

      // Log for debugging
      console.log('STORAGE LIST KEYPROCESSOR', { res, key })

      // Filter out error objects
      return (Array.isArray(res) ? res : [res] as any[])
        .filter(r => r && typeof r === 'object' && !('status' in r))
    }
    catch (error) {
      console.error(`Error in StorageListCache for key "${key}":`, error)
      return []
    }
  },
  { keyProcessor: pathKeyProcessor },
)
// #endregion

// #region StorageGetCache
const _StorageGetCacheExtensions = {
  path(this: CacheEntry<StorageNode>) {
    return new PathGuide(this.key)
  },
  download(
    this: CacheEntry<StorageNode>,
    cacheGroup?: CacheGroup,
  ): CacheEntry<_StorageDownloadCacheType> {
    return StorageDownloadCache.use(this.key, cacheGroup)
  },
  isDirectory(this: CacheEntry<StorageNode>): boolean {
    return this.value?.type === StorageNodeTypeEnum.Directory
  },
  isManual(this: CacheEntry<StorageNode>): boolean {
    return this.value?.type === StorageNodeTypeEnum.Manual
  },
  isFile(this: CacheEntry<StorageNode>): boolean {
    return this.value?.type === StorageNodeTypeEnum.File
  },
  async getReferences(this: CacheEntry<StorageNode>): Promise<ReferencesOutput> {
    return await storageApi.references(this.key)
  },
  async findParentManual(this: CacheEntry<StorageNode>): Promise<PathGuide | false> {
    const cg = new CacheGroup()
    const pg = PathGuide.from(this.key)

    // Cancel if theres a folder with .manual extension
    const manualFolder = pg.allAbove().find(p => p.name.endsWith('.manual'))

    if (!manualFolder)
      return false

    const x = manualFolder.useGet(cg)
    const value = await x.tryValueAsync
    if (value !== null && value.type === StorageNodeTypeEnum.Manual) {
      cg.clear()
      return manualFolder
    }
    cg.clear()
    return false
  },

  async isInsideTrashBin(this: CacheEntry<StorageNode>): Promise<boolean> {
    const pg = PathGuide.from(this.key)

    // Check if theres a bin folder above
    const trashFolder = pg.allAbove().find(p => p.name === 'binfolder.bin')

    return !!trashFolder
  },
  async canRestoreOriginalPath(this: CacheEntry<StorageNode>): Promise<{ restore: boolean, restorePath: string }> {
    const cg = new CacheGroup()
    const pg = PathGuide.from(this.key)

    let inTrash = false
    let highestBinLevel: null | number = null
    let binCount = 0
    let restorePath: string = ''

    const allAbove = pg.allAbove()

    for (const [level, path] of allAbove.entries()) {
      const isTrash = path.name === 'binfolder.bin'
      if (isTrash) {
        inTrash = true
        highestBinLevel = level
        restorePath = path.cd('..').toString()
        binCount++
      }
    }
    if (binCount > 1) {
      cg.clear()
      return { restore: false, restorePath }
    }
    if (!inTrash || highestBinLevel === null || binCount === 0) {
      cg.clear()
      return { restore: true, restorePath }
    }

    for (let i = 1; i < highestBinLevel; i++) {
      const currentNode = allAbove[i].useGet(cg)
      const value = await currentNode.tryValueAsync
      if (value !== null && value.type === StorageNodeTypeEnum.Directory) {
        cg.clear()
        return { restore: false, restorePath }
      }
    }
    cg.clear()
    return { restore: true, restorePath }
  },

  lsSiblings(
    this: CacheEntry<StorageNode>,
    cacheGroup?: CacheGroup,
  ): CacheEntry<StorageNode[]> {
    return StorageListCache.use(cdUp(this.key), cacheGroup)
  },
  ls(
    this: CacheEntry<StorageNode>,
    cacheGroup?: CacheGroup,
  ): CacheEntry<StorageNode[]> {
    if (_StorageGetCacheExtensions.isDirectory.call(this)) {
      return StorageListCache.use(this.key, cacheGroup)
    }
    else {
      throw new Error('Cannot ls non-directory, use lsSiblings instead')
    }
  },
  isMimeType(this: CacheEntry<StorageNode>, type: MimeCategory): boolean {
    return this.value?.mime !== undefined && isCategory(this.value?.mime, type)
  },
} as const

// const rcGetTenantMap = new Map<string, RetrieveCollector<RecordStringStorageNodeOrErrorValue>>()
// const rcListTenantMap = new Map<string, RetrieveCollector<Record<string, StorageNode[]>>>()

export const StorageGetCache = new CacheHandler<StorageNode, typeof _StorageGetCacheExtensions>(
  'StorageGet',
  async (key) => {
    const ret = await rcGet.get(key)
    // check if ret is error
    if (ret && (ret as any).status) {
      throw new ResponseError((ret as any).status, (ret as any).message)
    }
    if (ret && ('errno' in ret && ret.errno === 'ECONNREFUSED')) {
      throw new ResponseError(ret.errno as any, (ret as any))
    }
    return ret as unknown as StorageNode
  },
  {
    keyProcessor: pathKeyProcessor,
    afterRetrieve(key, value, valueOld) {
      // Check if invalidation cause is reload instead of delete (invalidated while usage > 0)

      // Sync the list cache with the new value

      // if (!equal(value, valueOld) && !(!value && !valueOld)) {
      //   console.log('XXXXXX', key, { value, valueOld })
      //   const parentPath = PathGuide.from(key).cd('..').toString()
      //   if (StorageListCache.exists(parentPath)) {
      //     const listRef = StorageListCache.get(parentPath)!
      //     console.log('BEFORE', [...listRef])
      //     const targetIndex = listRef.findIndex(x => x.name === PathGuide.from(key).name)
      //     const target = listRef[targetIndex]

      //     if (target) {
      //       if (value) {
      //         listRef[targetIndex] = value
      //       }
      //       else {
      //         listRef.splice(targetIndex, 1)
      //       }
      //     }
      //     else if (value) {
      //       listRef.push(value)
      //     }
      //     console.log('AFTER', [...listRef])
      //   }
      // }

      // Broken rn
      // if (reload && !equal(value, valueOld)) {
      //   const parentPath = PathGuide.from(key).cd('..').toString()
      //   if (StorageListCache.exists(parentPath)) {
      //     const list = StorageListCache.get(parentPath)
      //     if (list) {
      //       // Manually update the list cache
      //       // Either remove the old value or update it, depending on the new value
      //       const index = list.findIndex(x => x.name === PathGuide.from(key).name)
      //       if (index !== -1) {
      //         if (value) {
      //           list[index] = value
      //         }
      //         else {
      //           list.splice(index, 1)
      //         }
      //       }
      //       else if (value) {
      //         list.push(value)
      //       }
      // StorageListCache.set(key, list)
      //     }
      //     else {
      //       StorageListCache.invalidate(parentPath)
      //     }
      //   }
      // }
      if (!equal(value, valueOld)) {
        const parentPath = PathGuide.from(key).cd('..').toString()
        if (StorageListCache.exists(parentPath)) {
          // StorageListCache.invalidate(parentPath)
        }
      }
    },
    extensions: _StorageGetCacheExtensions,
  },
)
// #endregion

// #region ZitadelUserCache

export const ZitadelUserCache = new CacheHandler<{ user: UserInfo }>(
  'ZitadelUser',
  async (key) => {
    // Key format: authority;accessToken;userId
    const [authority, accessToken, userId] = key.split(';')

    if (!authority || !accessToken || !userId) {
      throw new Error('Invalid key format')
    }

    return await getUserInfo(accessToken, authority, userId)
  },
  {
  },
)
// #endregion

// const _fsHierarchyDebounce = debouncedRef(StorageListCache._operation, 50)

// Combine the two caches into a hierarchical object featuring a "children" property
